import { useEffect, useState, useRef, useMemo } from "react";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import moment from "moment-timezone";

const WORK_START_HOUR = 9;
const TIMEZONE = "Asia/Singapore";

const getDateKey = (date) => moment(date).format("YYYY-MM-DD");

const isWeekend = (date) => {
  const day = moment(date).day();
  if (JSON.parse(process.env.REACT_APP_TEST_SERVER || false)) return false;
  return day === 0;
};

const isHoliday = (date, holidaySet) => holidaySet.has(getDateKey(date));

const isWorkingDay = (date, holidaySet) =>
  !isWeekend(date) && !isHoliday(date, holidaySet);

const isWorkingHour = (date, WORK_END_HOUR = 18, minutes = 0, seconds = 0) => {
  const current = moment(date);
  const start = current.clone().hour(WORK_START_HOUR).minute(0).second(0);
  let end = null;
  if (WORK_END_HOUR == null && minutes == null && seconds == null) {
    end = current.clone().add(24, "hours");
  } else {
    end = current.clone().hour(WORK_END_HOUR).minute(minutes).second(seconds);
  }

  return current.isBetween(start, end, null, "[)");
};

const calculateElapsedWorkingTime = (
  startDate,
  now,
  holidaySet,
  WORK_END_HOUR = 18,
  minutes = 0,
  seconds = 0
) => {
  let current = moment(startDate).startOf("day");
  const end = moment(now);
  let elapsed = 0;

  while (current.isBefore(end, "day") || current.isSame(end, "day")) {
    if (isWorkingDay(current, holidaySet)) {
      const workStart = current
        .clone()
        .hour(WORK_START_HOUR)
        .minute(0)
        .second(0);
      let workEnd = null;
      if (WORK_END_HOUR == null && minutes == null && seconds == null) {
        workEnd = current.clone().add(24, "hours");
      } else {
        workEnd = current
          .clone()
          .hour(WORK_END_HOUR)
          .minute(minutes)
          .second(seconds);
      }
      const actualStart = moment.max(moment(startDate), workStart);
      const actualEnd = moment.min(end, workEnd);

      if (actualEnd.isAfter(actualStart)) {
        elapsed += actualEnd.diff(actualStart);
      }
    }

    current.add(1, "day").startOf("day");
  }

  return elapsed;
};

const formatTime = (ms) => {
  const negative = ms < 0;
  const totalSec = Math.abs(Math.floor(ms / 1000));

  const days = Math.floor(totalSec / (3600 * 24));
  const hrs = Math.floor((totalSec % (3600 * 24)) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);

  return `${negative ? "-" : ""}${String(days).padStart(2, "0")}:${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const Timer = ({
  startDate,
  holidays = [],
  endHours,
  id,
  isExpired,
  status,
  WORK_END_HOUR = 18,
  minutes = 0,
  seconds = 0,
  DURATION,
  TYPE,
  ticketStatus,
  endTime,
}) => {
  const colorScheme = {
    2: ["#FF6700", "#FFFFFF"], // Orange for < 3 hours
    3: ["#00ff359c", "#000000"], // Green for >= 3 hours
    4: ["#ff00009c", "#000000"], // Red for < 0 hours
    10: ["#f0ff009c", "#000000"],
  };
  const [whichTimerRunning, setWhichTimerRunning] = useState({
    0: false,
    2: false,
    3: false,
    4: false,
  });

  const whichTimerRunningRef = useRef(whichTimerRunning);
  useEffect(() => {
    setWhichTimerRunning((prev) => {
      const updated = { ...prev, [status]: true };
      whichTimerRunningRef.current = updated;
      return updated;
    });
  }, [status]);

  const TOTAL_DURATION_MS = endHours * DURATION * 60 * 60 * 1000;
  const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
  const [remainingMs, setRemainingMs] = useState(TOTAL_DURATION_MS);
  const [isActive, setIsActive] = useState(false);

  // Memoize holidaysKey to create a stable string for dependency comparison
  const holidaysKey = useMemo(() => JSON.stringify(holidays), [holidays]);

  // Memoize holidaySet to avoid recreating it on every render
  const holidaySet = useMemo(
    () => new Set(holidays.map(getDateKey)),
    [holidaysKey]
  );

  //   useEffect(() => {
  //   const now = moment();
  //   const shouldPause =
  //     ticketStatus === "1" || (endTime && moment(endTime).isBefore(now));

  //   if (shouldPause) {
  //     const actualEnd =
  //       endTime && moment(endTime).isBefore(now) ? moment(endTime) : now;

  //     const elapsed = calculateElapsedWorkingTime(
  //       startDate,
  //       actualEnd,
  //       holidaySet,
  //       WORK_END_HOUR,
  //       minutes,
  //       seconds
  //     );

  //     setRemainingMs(TOTAL_DURATION_MS - elapsed);
  //     setIsActive(false);
  //     return;
  //   }

  //   const interval = setInterval(() => {
  //     const now = moment();

  //     const elapsed = calculateElapsedWorkingTime(
  //       startDate,
  //       now,
  //       holidaySet,
  //       WORK_END_HOUR,
  //       minutes,
  //       seconds
  //     );

  //     const remaining = TOTAL_DURATION_MS - elapsed;
  //     setRemainingMs(remaining);

  //     const active =
  //       isWorkingDay(now, holidaySet) &&
  //       isWorkingHour(now, WORK_END_HOUR, minutes, seconds) &&
  //       remaining > 0;

  //     setIsActive(active);
  //   }, 1000);

  //   return () => clearInterval(interval);
  // }, [
  //   startDate,
  //   holidays,
  //   WORK_END_HOUR,
  //   minutes,
  //   seconds,
  //   TOTAL_DURATION_MS,
  //   ticketStatus,
  //   endTime,
  // ]);

  useEffect(() => {
    const now = moment();
    const shouldPause =
      ticketStatus === "1" || (endTime && moment(endTime).isBefore(now));

    const actualEnd =
      endTime && moment(endTime).isBefore(now) ? moment(endTime) : now;

    const initialElapsed = calculateElapsedWorkingTime(
      startDate,
      shouldPause ? actualEnd : now,
      holidaySet,
      WORK_END_HOUR,
      minutes,
      seconds
    );

    const initialRemaining = TOTAL_DURATION_MS - initialElapsed;
    setRemainingMs(initialRemaining);

    const active =
      !shouldPause &&
      isWorkingDay(now, holidaySet) &&
      isWorkingHour(now, WORK_END_HOUR, minutes, seconds) &&
      initialRemaining > 0;

    setIsActive(active);

    if (shouldPause) return;

    const interval = setInterval(() => {
      const now = moment();

      const elapsed = calculateElapsedWorkingTime(
        startDate,
        now,
        holidaySet,
        WORK_END_HOUR,
        minutes,
        seconds
      );

      const remaining = TOTAL_DURATION_MS - elapsed;
      setRemainingMs(remaining);

      const active =
        isWorkingDay(now, holidaySet) &&
        isWorkingHour(now, WORK_END_HOUR, minutes, seconds) &&
        remaining > 0;

      setIsActive(active);
    }, 1000);

    return () => clearInterval(interval);
  }, [
    startDate,
    holidaysKey,
    WORK_END_HOUR,
    minutes,
    seconds,
    TOTAL_DURATION_MS,
    ticketStatus,
    endTime,
    holidaySet,
  ]);

  const colorKey = remainingMs < 0 ? "4" : remainingMs < THREE_HOURS_MS ? "2" : "3";

  return (
    <div
      style={{
        background: colorScheme[colorKey]?.[0],
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        color: colorScheme[colorKey]?.[1],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "72px",
        padding: "2px 0px",
      }}
    >
      <span
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {remainingMs < 0 ? (
          formatTime(remainingMs)
        ) : (
          <>
            {isActive ? (
              <PauseIcon sx={{ fontSize: "100%" }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: "100%" }} />
            )}
            {formatTime(remainingMs)}
          </>
        )}
      </span>
    </div>
  );
};

export default Timer;
